import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Building2,
  CornerUpLeft,
  MessageSquare,
  Paperclip,
  Send,
  Sparkles,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { formatIstTime, relativeTime } from '@gbv/utils';
import { MAX_UPLOAD_BYTES } from '@gbv/types';
import { Badge, Button, Spinner, Textarea, cn, useToast } from '@gbv/ui';
import { api, ApiRequestError, describeApiError } from '../lib/api';
import { PersonalChat } from './PersonalChat';
import { AssistantPanel } from './assistant/AssistantPanel';
import { useAuth } from '../stores/auth';
import { onServerEvent } from '../lib/socket';

/**
 * The floating chat launcher.
 *
 * Two conversations, one button, reachable from every screen — because the
 * moment you need to ask a colleague about an account is while you are looking
 * at it, not after navigating away from it.
 *
 *   Client   the thread the client can see and reply to
 *   Team     the internal channel for this account, which they cannot
 *
 * The two are visually distinct on purpose. Posting an internal remark into
 * the client's thread is the one mistake this component must make hard, so the
 * active tab is stated in the composer's own placeholder rather than only in a
 * tab heading above it.
 */

interface TeamMessage {
  id: string;
  body: string;
  senderId: string;
  senderName: string;
  isMine: boolean;
  replyToId: string | null;
  replyToBody: string | null;
  replyToSender: string | null;
  editedAt: string | null;
  fileUrl: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
}

interface ClientMessage {
  id: string;
  body: string;
  senderType: 'CLIENT' | 'EMPLOYEE';
  senderName: string;
  fileUrl: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
}

/**
 * Official is about a client — the account team, and the client themselves.
 * Personal is about people: a direct line to a colleague, and groups they
 * make. Two different audiences, so the switch is above the tabs rather
 * than a third tab beside them.
 */
type Mode = 'official' | 'personal' | 'help';

type Tab = 'client' | 'team';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

export function FloatingChat() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const orgId = useAuth((s) => s.activeOrgId);
  const membership = useAuth((s) => s.activeMembership());

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('team');
  const [mode, setMode] = useState<Mode>('official');
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<TeamMessage | null>(null);
  const [pendingFile, setPendingFile] = useState<{ url: string; fileName: string; fileSizeBytes: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Unread for the badge. Polled rather than pushed so the count is right
  // after a reload, and cheap enough at one request a minute.
  const unread = useQuery({
    queryKey: ['chat-unread', orgId],
    queryFn: () => api.get<{ team: number; client: number }>('/workspace/chat/unread'),
    enabled: Boolean(orgId),
    refetchInterval: 60_000,
  });

  const personalUnread = useQuery({
    queryKey: ['personal-unread'],
    queryFn: () => api.get<{ unread: number }>('/internal/chat/unread'),
    refetchInterval: 60_000,
  });

  const team = useQuery({
    queryKey: ['team-chat', orgId],
    queryFn: () =>
      api.get<{ messages: TeamMessage[]; unread: number; participants: Array<{ id: string; name: string }> }>(
        '/workspace/team-chat',
      ),
    enabled: Boolean(orgId) && open && tab === 'team',
  });

  const client = useQuery({
    queryKey: ['client-chat', orgId],
    queryFn: () => api.get<{ messages: ClientMessage[] }>('/workspace/chat'),
    enabled: Boolean(orgId) && open && tab === 'client',
  });

  // A colleague posting shows up without a reload.
  useEffect(
    () =>
      onServerEvent('team:message', () => {
        void queryClient.invalidateQueries({ queryKey: ['team-chat'] });
        void queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
      }),
    [queryClient],
  );

  // Opening a tab is reading it.
  useEffect(() => {
    if (!open || !orgId) return;
    const path = tab === 'team' ? '/workspace/team-chat/read' : '/workspace/chat/read';
    void api.post(path).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
    });
  }, [open, tab, orgId, queryClient]);

  // Land on the newest message, the way every chat does.
  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [open, tab, team.data, client.data]);

  const send = useMutation({
    mutationFn: async () => {
      const body = draft.trim();
      let fileUrl: string | null = null;
      let fileName: string | null = null;
      let fileSizeBytes: number | null = null;

      if (pendingFile) {
        fileUrl = pendingFile.url;
        fileName = pendingFile.fileName;
        fileSizeBytes = pendingFile.fileSizeBytes;
      }

      if (tab === 'team') {
        return api.post('/workspace/team-chat', {
          body,
          replyToId: replyTo?.id ?? null,
          fileUrl,
          fileName,
          fileSizeBytes,
        });
      }

      return api.post('/workspace/chat', { body, fileUrl, fileName, fileSizeBytes });
    },
    onSuccess: () => {
      setDraft('');
      setReplyTo(null);
      setPendingFile(null);
      void queryClient.invalidateQueries({
        queryKey: [tab === 'team' ? 'team-chat' : 'client-chat'],
      });
    },
    onError: (err) => toast.error('Message not sent', describeApiError(err)),
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('That file is too large', `The limit is ${MAX_UPLOAD_BYTES / 1024 / 1024}MB.`);
      return;
    }
    try {
      const form = new FormData();
      form.append('file', file);
      const endpoint = tab === 'team' ? '/internal/attachments' : '/files';
      const result = await api.postForm<{ url: string; fileName: string; fileSizeBytes: number }>(
        endpoint,
        form,
        tab === 'client' ? { headers: { 'X-Org-Id': orgId! } } : undefined,
      );
      setPendingFile(result);
    } catch (err) {
      toast.error('Upload failed', err instanceof ApiRequestError ? err.message : 'Try again in a moment.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const totalUnread = (unread.data?.team ?? 0) + (unread.data?.client ?? 0);

  const messages = useMemo(() => {
    if (tab === 'team') return team.data?.messages ?? [];
    return client.data?.messages ?? [];
  }, [tab, team.data, client.data]);

  const loading = tab === 'team' ? team.isLoading : client.isLoading;

  if (!orgId) return null;

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full shadow-overlay transition',
          open ? 'bg-ink text-white' : 'bg-vir text-white hover:bg-vir-dark',
        )}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <X size={19} /> : <MessageSquare size={19} />}
        {!open && totalUnread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-20 right-5 z-40 flex h-[540px] max-h-[calc(100vh-7rem)] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-card border border-line bg-white shadow-overlay">
          <div className="border-b border-line px-3 pt-2">
            {/*
              The switch sits above the tabs because it changes who the panel
              is about, not which thread within it. Official is a client;
              personal is a person.
            */}
            <div className="mb-2 flex rounded-control bg-surface-sunken p-0.5">
              {(
                [
                  ['official', 'Official', Briefcase],
                  ['personal', 'Personal', UserRound],
                  ['help', 'Help', Sparkles],
                ] as const
              ).map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-[5px] px-2 py-1 text-[12px] font-semibold transition-colors',
                    mode === key
                      ? 'bg-white text-ink shadow-sm'
                      : 'text-ink-soft hover:text-ink',
                  )}
                >
                  <Icon size={13} />
                  {label}
                  {key === 'personal' && (personalUnread.data?.unread ?? 0) > 0 && (
                    <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
                      {personalUnread.data!.unread > 99 ? '99+' : personalUnread.data!.unread}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {mode === 'official' && (
              <>
                <p className="mb-1.5 truncate text-[11px] text-ink-soft">
                  {membership?.orgName ?? 'This client'}
                </p>
                <div className="flex gap-1">
                  <TabButton
                    active={tab === 'team'}
                    onClick={() => setTab('team')}
                    icon={Users}
                    label="Team"
                    unread={unread.data?.team ?? 0}
                  />
                  <TabButton
                    active={tab === 'client'}
                    onClick={() => setTab('client')}
                    icon={Building2}
                    label="Client"
                    unread={unread.data?.client ?? 0}
                  />
                </div>
              </>
            )}
          </div>

          {mode === 'help' ? (
            <AssistantPanel />
          ) : mode === 'personal' ? (
            <PersonalChat />
          ) : (
            <>
          {/* An unmissable reminder of who can read what you are about to type. */}
          <p
            className={cn(
              'px-3 py-1.5 text-[10px]',
              tab === 'team'
                ? 'bg-surface-page text-ink-muted'
                : 'bg-bee-light text-ink',
            )}
          >
            {tab === 'team'
              ? 'Internal — the client cannot see this channel.'
              : 'The client reads this thread and can reply.'}
          </p>

          <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <MessageSquare size={20} className="text-ink-soft" />
                <p className="mt-2 text-sm text-ink">
                  {tab === 'team' ? 'No internal messages yet' : 'No messages with this client yet'}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  {tab === 'team'
                    ? 'Ask a colleague something about this account.'
                    : 'Say hello — they see this in their portal.'}
                </p>
              </div>
            ) : (
              messages.map((m) => {
                const mine = tab === 'team' ? (m as TeamMessage).isMine : (m as ClientMessage).senderType === 'EMPLOYEE';
                const teamMsg = tab === 'team' ? (m as TeamMessage) : null;
                const clientMsg = tab === 'client' ? (m as ClientMessage) : null;

                return (
                  <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                    <div
                      className={cn(
                        'group max-w-[85%] rounded-card px-3 py-2',
                        mine ? 'bg-vir text-white' : 'bg-surface-page text-ink',
                      )}
                    >
                      {!mine && (
                        <p
                          className={cn(
                            'mb-0.5 text-[10px] font-semibold',
                            mine ? 'text-white/70' : 'text-ink-soft',
                          )}
                        >
                          {m.senderName}
                        </p>
                      )}

                      {teamMsg?.replyToBody && (
                        <p
                          className={cn(
                            'mb-1 truncate border-l-2 pl-1.5 text-[10px]',
                            mine ? 'border-white/40 text-white/70' : 'border-line text-ink-soft',
                          )}
                        >
                          {teamMsg.replyToSender}: {teamMsg.replyToBody}
                        </p>
                      )}

                      {(teamMsg?.fileUrl || clientMsg?.fileUrl) && (
                        <a
                          href={(teamMsg || clientMsg)!.fileUrl!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            'mb-1.5 flex items-center gap-1.5 rounded-pill px-2 py-1 text-[11px]',
                            mine ? 'bg-white/15 text-white' : 'bg-line text-ink-muted',
                          )}
                        >
                          <Paperclip size={10} />
                          <span className="min-w-0 flex-1 truncate">{(teamMsg || clientMsg)!.fileName}</span>
                          {(teamMsg || clientMsg)!.fileSizeBytes && <span className="shrink-0 opacity-80">{formatFileSize((teamMsg || clientMsg)!.fileSizeBytes!)}</span>}
                        </a>
                      )}

                      <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>

                      <p
                        className={cn(
                          'mt-0.5 text-[9px]',
                          mine ? 'text-white/60' : 'text-ink-soft',
                        )}
                      >
                        {formatIstTime(new Date(m.createdAt))}
                        {teamMsg?.editedAt && ' · edited'}
                      </p>

                      {tab === 'team' && teamMsg && (
                        <button
                          type="button"
                          onClick={() => setReplyTo(teamMsg)}
                          className={cn(
                            'mt-1 hidden items-center gap-1 text-[10px] group-hover:flex',
                            mine ? 'text-white/80' : 'text-ink-muted',
                          )}
                        >
                          <CornerUpLeft size={9} />
                          Reply
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {replyTo && tab === 'team' && (
            <div className="flex items-center gap-2 border-t border-line bg-surface-page px-3 py-1.5">
              <CornerUpLeft size={11} className="shrink-0 text-ink-soft" />
              <span className="min-w-0 flex-1 truncate text-[11px] text-ink-muted">
                {replyTo.senderName}: {replyTo.body}
              </span>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="shrink-0 text-ink-soft hover:text-ink"
                aria-label="Cancel reply"
              >
                <X size={11} />
              </button>
            </div>
          )}

          <div className="border-t border-line p-2">
            {pendingFile && (
              <div className="mb-1.5 flex items-center gap-2 rounded-control border border-line bg-surface-page px-2.5 py-1.5 text-xs">
                <Paperclip size={12} className="shrink-0 text-ink-soft" />
                <span className="min-w-0 flex-1 truncate text-ink">{pendingFile.fileName}</span>
                <span className="shrink-0 text-ink-soft">{formatFileSize(pendingFile.fileSizeBytes)}</span>
                <button
                  type="button"
                  onClick={() => setPendingFile(null)}
                  className="shrink-0 rounded p-0.5 text-ink-soft transition hover:text-ink"
                  aria-label="Remove attachment"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            <Textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — what everyone
                // already expects from a chat box.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (draft.trim() || pendingFile) send.mutate();
                }
              }}
              placeholder={
                tab === 'team'
                  ? 'Message the team about this client…'
                  : `Message ${membership?.orgName ?? 'the client'}…`
              }
              aria-label={tab === 'team' ? 'Message the team' : 'Message the client'}
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-[10px] text-ink-soft transition hover:text-ink"
                >
                  <Paperclip size={11} />
                  Attach
                </button>
                <span className="text-[10px] text-ink-soft">Enter to send</span>
              </div>
              <Button
                size="sm"
                leftIcon={<Send size={12} />}
                onClick={() => send.mutate()}
                isLoading={send.isPending}
                disabled={draft.trim().length === 0 && !pendingFile}
              >
                Send
              </Button>
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  unread,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Users;
  label: string;
  unread: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-t px-3 py-2 text-xs font-semibold transition',
        active
          ? 'border-b-2 border-vir text-ink'
          : 'border-b-2 border-transparent text-ink-muted hover:text-ink',
      )}
    >
      <Icon size={13} />
      {label}
      {unread > 0 && (
        <Badge tone="danger">{unread > 9 ? '9+' : unread}</Badge>
      )}
    </button>
  );
}
